import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connectLean } from 'lean-redux';
import { withRouter } from 'react-router';
import { withApollo } from 'react-apollo';
import FocusLock from 'react-focus-lock';
import cn from 'classnames';
import Button from 'chameleon/ui-stack/ui/base/button';
import { UNITS as COPY } from 'chameleon/ui-stack/utilities/copy';
import { ShareContent as AdminShareContent } from '../admin';
import ActionButtons from './ActionButtons';
import CopyDetailsToClipboard from 'legacy/src/utility/CopyDetailsToClipboard';
import ClassesSelector from 'legacy/src/utility/ClassesSelector';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import dateTimeFormat from 'legacy/src/utility/DateTime';
import dayjs from 'dayjs';
import './Share.scss';

/**
 * Draws unit sharing
 * */
/**
 * @note
 * AP daily video resources will not use this modal for assigning
 * Check the work in FY-19867 @Mihai
 * */
class Share extends PureComponent {
  static displayName = 'Share';

  static propTypes = {
    item: PropTypes.object,
    scope: PropTypes.array,
    items: PropTypes.array.isRequired,
    isEmbeddedVideo: PropTypes.bool,
    shareableLink: PropTypes.string,
    noClassAlert: PropTypes.element,
    hasNoClasses: PropTypes.bool,
    modalTitle: PropTypes.string,
  };

  static defaultProps = {
    item: null,
    scope: [],
    isEmbeddedVideo: false,
    shareableLink: '',
    noClassAlert: Fragment,
    hasNoClasses: false,
    modalTitle: '',
  };

  state = {
    showPopOver: false,
  };

  /**
   * On popover button click open popver
   * */
  onActionClick = () => {
    const { showPopOver: currentStatus } = this.state;
    this.setState({ showPopOver: !currentStatus });
  };

  /**
   * On Cancel button click action
   * */
  onCancelClick = () => {
    const { setSelectedItems, savedClasses } = this.props;
    this.setState({ showPopOver: false });
    setSelectedItems(savedClasses || []);
    this.props.onClose && this.props.onClose();
  };

  /**
   * On save button click handler
   * Saves the selected classes
   * @note callback for <SaveButton />
   * @param {array} selectedItems Selected classes array
   * */
  onSaveClick = async (selectedItems, previouslySelected) => {
    const {
      setSavedClasses,
      setNewAssignData,
      setSavedClassesWithName,
      item,
      params: { subject },
      client,
      save,
    } = this.props;
    let selectedDifference = selectedItems.filter(
      (x) => !previouslySelected.includes(x),
    );
    await setSavedClasses(selectedItems);
    await setNewAssignData(selectedDifference, subject, item.id);
    const newSavedClasses = await save(selectedItems, item, subject, client);

    setSavedClassesWithName(
      newSavedClasses.sharedWith?.map((c) => ({
        id: c.sectionId,
        name: c.sectionName,
      })),
    );

    this.setState({ showPopOver: false });
    this.props.onClose && this.props.onClose();
  };

  /**
   * Callback for opening and closing the popover
   * @param {boolean} isOpen
   * */
  onPopOverClick = (isOpen) => {
    if (!isOpen) {
      this.restoreSavedClasses();
    }
  };

  /**
   * Restore saved classes in selection/selected boxes after canceling
   * */
  restoreSavedClasses = () => {
    this.props.setSelectedItems(this.props.savedClasses || []);
  };

  render() {
    const {
      savedClasses = [],
      item,
      scope,
      disabled,
      items,
      userData: { isAdmin, isCoordinator, isTeacher },
      isEmbeddedVideo,
      shareableLink,
      children,
      hasNoClasses,
      noClassAlert,
      modalTitle,
      subject,
      cachedAssignData,
      isPreApSubject,
    } = this.props;
    const { showPopOver } = this.state;
    const hasSavedClasses = savedClasses.length > 0;

    const isShared = hasSavedClasses && !disabled;
    const { SHARE, ADMIN_COORDINATOR } = COPY;
    const isAdminOrCoordinator = isAdmin || isCoordinator;

    const labelText = item.__typename && SHARE[item.__typename].LABEL;
    const descriptionText = SHARE[item.__typename].DESCRIPTION;
    const notificationText =
      isAdminOrCoordinator && item.__typename === 'Unit'
        ? ADMIN_COORDINATOR.UNIT_NOTIFICATION
        : '';

    const childrenProps = {
      isShared,
      disabled,
      onClick: this.onActionClick,
      isAssigned: hasSavedClasses,
      isPreApSubject,
      item,
      subject,
    };

    const assignedData = {};
    isTeacher &&
      isEmbeddedVideo &&
      items.map((section) => {
        assignedData[section.id] = {
          timezone: section.timezone || 'America/New_York',
        };
        section.resourcesAssigned.map((resourceObj) => {
          const { resource, type, subjectId } = resourceObj;
          if (
            item.id === resource &&
            type === 'EmbeddedVideoResource' &&
            subjectId === subject.id
          ) {
            assignedData[section.id] = {
              ...resourceObj,
              ...assignedData[section.id],
            };
          }
        });
      });
    const cachedData =
      (item &&
        subject &&
        ((cachedAssignData || {})[subject.id] || {})[item.id]) ||
      {};
    const saveButtonText = savedClasses.length > 0 ? 'Update' : 'Assign';
    const teacherWithoutClasses = !isAdminOrCoordinator && hasNoClasses;

    return (
      <Fragment>
        {children && React.cloneElement(children, childrenProps)}
        {showPopOver && (
          <FocusLock>
            <ConfirmDialog
              title={teacherWithoutClasses ? modalTitle : labelText}
              onClose={this.onCancelClick}
              contentWithSpacing={false}
              wrapperClassName={
                isAdminOrCoordinator ? 'admin-cord-override' : ''
              }
            >
              {teacherWithoutClasses && Boolean(noClassAlert) ? (
                <Fragment>{noClassAlert}</Fragment>
              ) : (
                <Fragment>
                  {isAdminOrCoordinator ? (
                    <AdminShareContent isEmbeddedVideo={isEmbeddedVideo} />
                  ) : (
                    <ClassesSelector
                      showHeader={false}
                      description={descriptionText}
                      descriptionClassName="m-4.6 text-14 font-medium text-gray-700"
                      notificationText={notificationText}
                      items={items}
                      hasNoClasses={hasNoClasses}
                      scope={scope}
                      isEmbeddedVideo={isEmbeddedVideo}
                      resourceShareData={assignedData}
                      savedClasses={savedClasses}
                      cachedAssignData={cachedData}
                    />
                  )}
                  {isEmbeddedVideo && (
                    <p className="dark-bold-text">Share Video</p>
                  )}
                  <div
                    className={cn({
                      'copy-and-share-wrapper': isEmbeddedVideo,
                    })}
                  >
                    {isEmbeddedVideo && (
                      <div className="copy-shared-link">
                        <span>{shareableLink.slice(8)}</span>
                        <CopyDetailsToClipboard
                          text={shareableLink}
                          notificationText="Link has been copied to clipboard"
                        >
                          <Button isSecondary>Copy</Button>
                        </CopyDetailsToClipboard>
                      </div>
                    )}
                    <ActionButtons
                      className="h-25 flex items-center justify-end mr-4.6"
                      onSave={this.onSaveClick}
                      onCancel={this.onCancelClick}
                      scope={scope}
                      hasAlert={item.__typename === 'Unit'}
                      saveButtonText={isEmbeddedVideo ? saveButtonText : ''}
                      isEmbeddedVideo={isEmbeddedVideo}
                      resourceShareData={assignedData}
                      savedClasses={savedClasses}
                      cachedAssignData={cachedData}
                    />
                  </div>
                </Fragment>
              )}
            </ConfirmDialog>
          </FocusLock>
        )}
      </Fragment>
    );
  }
}

export default withApollo(
  withRouter(
    connectLean({
      mapState(state) {
        const {
          selectedItems,
          savedClasses,
          cachedAssignData,
          savedClassesWithName,
        } = state;

        return {
          selectedItems,
          savedClasses,
          cachedAssignData,
          savedClassesWithName,
        };
      },
      setSavedClassesWithName(items) {
        this.setState({
          savedClassesWithName: items,
        });
      },
      setSavedClasses(items) {
        this.setState({
          savedClasses: items,
        });
      },
      setSelectedItems(items) {
        this.setState({
          selectedItems: items,
        });
      },
      setNewAssignData(items, subject, resourceId) {
        const assignData = items.reduce((obj, section) => {
          obj[section] = dateTimeFormat(dayjs(), 'dateAndTimeZoned');
          return obj;
        }, {});
        this.setState((prevState) => {
          const prevCache =
            ((prevState.cachedAssignData || {})[subject] || {})[resourceId] ||
            {};
          return {
            cachedAssignData: {
              [subject]: {
                [resourceId]: {
                  ...prevCache, // maintain what was there
                  ...assignData, // add to what was there
                },
              },
            },
          };
        });
      },
    })(Share),
  ),
);
